// TO-DO: find a better way than hardcoding the error codes [POS-1907]
export const ApiErrorCodesEnum = {
  // Generic errors
  BAD_REQUEST: "bad_request",
  NOT_FOUND: "not_found",
  NOT_ACCEPTABLE: "not_acceptable",
  UNAUTHORIZED: "unauthorized",
  FORBIDDEN: "forbidden",
  CONFLICT: "conflict",
  UNPROCESSABLE_ENTITY: "unprocessable_entity",
  INTERNAL_SERVER_ERROR: "internal_server_error",

  // User related errors
  USER_NOT_FOUND: "user_not_found",
  POS_USER_NOT_CONFIGURED: "pos_user_not_configured",

  // Supplier related errors
  SUPPLIER_NOT_FOUND: "supplier_not_found",

  // Product colors related errors
  PRODUCT_COLORS_UNAVAILABLE: "product_colors_unavailable",
  PRODUCT_COLORS_GENERATION_FAILED: "product_colors_generation_failed",
  PRODUCT_COLORS_STORAGE_FAILED: "product_colors_storage_failed",

  // Session related errors
  SESSION_ALREADY_CLOSED: "session_already_closed",
  SESSION_NOT_ON_TILL: "session_not_on_till",

  // Child related errors
  CHILD_NOT_FOUND: "child_not_found",
  CHILD_MULTIPLE_FOUND: "child_multiple_found",
  CHILD_IN_WRONG_SALESPLACE: "child_in_wrong_salesplace",

  // Offer related errors
  OFFER_NOT_FOUND: "offer_not_found",
  OFFER_PRODUCTS_NOT_AVAILABLE: "offer_products_not_available",

  // Payment related errors
  PAYMENT_METHOD_NOT_ALLOWED: "payment_method_not_allowed",
  PAYMENT_USER_BALANCE_INSUFFICIENT: "payment_user_balance_insufficient",

  PAYMENT_ADYEN_ABORTED: "payment_adyen_aborted",
  PAYMENT_ADYEN_BUSY: "payment_adyen_busy",
  PAYMENT_ADYEN_CANCELLED: "payment_adyen_cancelled",
  PAYMENT_ADYEN_DEVICE_OUT: "payment_adyen_device_out",
  PAYMENT_ADYEN_IN_PROGRESS: "payment_adyen_in_progress",
  PAYMENT_ADYEN_INSERTED_CARD: "payment_adyen_inserted_card",
  PAYMENT_ADYEN_INVALID_CARD: "payment_adyen_invalid_card",
  PAYMENT_ADYEN_LOGGED_OUT: "payment_adyen_logged_out",
  PAYMENT_ADYEN_MESSAGE_FORMAT: "payment_adyen_message_format",
  PAYMENT_ADYEN_NOT_ALLOWED: "payment_adyen_not_allowed",
  PAYMENT_ADYEN_NOT_FOUND: "payment_adyen_not_found",
  PAYMENT_ADYEN_PAYMENT_RESTRICTION: "payment_adyen_payment_restriction",
  PAYMENT_ADYEN_REFUSAL: "payment_adyen_refusal",
  PAYMENT_ADYEN_UNAVAILABLE_DEVICE: "payment_adyen_unavailable_device",
  PAYMENT_ADYEN_UNAVAILABLE_SERVICE: "payment_adyen_unavailable_service",
  PAYMENT_ADYEN_UNREACHABLE_HOST: "payment_adyen_unreachable_host",
  PAYMENT_ADYEN_WRONG_PIN: "payment_adyen_wrong_pin",

  // Order related errors
  ORDER_ALREADY_PAID: "order_already_paid",
  ORDER_PAYMENT_IN_PROGRESS: "order_payment_in_progress",
  ORDER_VALIDATION_HB_ALLOWANCE_EXCEEDED:
    "order_validation_hb_allowance_exceeded",
  ORDER_VALIDATION_MINIMUM_AMOUNT_NOT_REACHED:
    "order_validation_minimum_amount_not_reached",
  ORDER_VALIDATION_DEPOSIT_MISMATCH: "order_validation_deposit_mismatch",

  // User limits related errors
  USER_LIMIT_OVER: "user_limit_over",

  // Collect related errors
  COLLECT_FAILED_TO_UPDATE_ORDER: "collect_failed_to_update_order",
  COLLECT_INVALID_COLLECTION_EVENT: "collect_invalid_collection_event",
  COLLECT_PRODUCT_QUANTITY_EXCEEDED: "collect_product_quantity_exceeded",
  COLLECT_DUPLICATE_MODULES_IN_BASKET: "collect_duplicate_modules_in_basket",

  // Card identifier related errors
  CHILD_CARD_IN_USE: "child_card_in_use",
  CARD_INVALID: "card_invalid"
} as const;

export type ApiErrorCode =
  (typeof ApiErrorCodesEnum)[keyof typeof ApiErrorCodesEnum];

export type ApiErrorResponse = {
  body: {
    message: string;
    code: ApiErrorCode;
    productIds?: string[];
  };
  status: number;
};

export function isApiError(error: unknown): error is ApiErrorResponse {
  return (
    typeof error === "object" &&
    error !== null &&
    "body" in error &&
    "status" in error &&
    typeof error.body === "object" &&
    error.body !== null &&
    "code" in error.body
  );
}

export function isApiErrorCode<C extends ApiErrorCode>(
  error: unknown,
  code: C
): error is ApiErrorResponse & { body: { code: C } } {
  return isApiError(error) && error.body.code === code;
}

/**
 * The server refused an order over the deposit the till declared. A missing
 * declaration comes back as a plain 400 with no code, logged server side.
 */
export const DEPOSIT_REJECTION_CODES = [
  ApiErrorCodesEnum.ORDER_VALIDATION_DEPOSIT_MISMATCH
] as const;

export function isDepositRejectionError(
  error: unknown
): error is ApiErrorResponse & {
  body: { code: (typeof DEPOSIT_REJECTION_CODES)[number] };
} {
  return DEPOSIT_REJECTION_CODES.some((code) => isApiErrorCode(error, code));
}

const NETWORK_ERROR_MESSAGES = [
  "Failed to fetch", // Chromium
  "Network request failed", // React Native
  "Load failed", // Safari 17+
  "NetworkError when attempting to fetch resource." // Firefox
];

export function isNetworkError(error: unknown): error is TypeError {
  return (
    error instanceof TypeError &&
    NETWORK_ERROR_MESSAGES.some((message) => error.message.startsWith(message))
  );
}

const TRANSIENT_ERROR_MESSAGES = [
  ...NETWORK_ERROR_MESSAGES,
  "The network connection was lost.", // WKWebView
  "The Internet connection appears to be offline.", // WKWebView
  "The request timed out."
];

const TRANSIENT_ERROR_NAMES = ["AbortError", "TimeoutError"];

function getStringProperty(error: unknown, key: string): string | null {
  if (typeof error !== "object" || error === null || !(key in error)) {
    return null;
  }

  const value = (error as Record<string, unknown>)[key];

  return typeof value === "string" ? value : null;
}

/**
 * Widens `isNetworkError` to the failures that share its meaning but not its
 * shape: aborts and timeouts are `DOMException`/`TimeoutError`, never
 * `TypeError`, so an `instanceof` check would drop them.
 */
export function isTransientRequestError(error: unknown): boolean {
  if (isNetworkError(error)) {
    return true;
  }

  const name = getStringProperty(error, "name");
  if (name !== null && TRANSIENT_ERROR_NAMES.includes(name)) {
    return true;
  }

  const message = getStringProperty(error, "message");

  return (
    message !== null &&
    TRANSIENT_ERROR_MESSAGES.some((candidate) => message.startsWith(candidate))
  );
}

/**
 * Reads the status off a thrown ts-rest response, which can carry a 5xx body
 * without the `code` that `isApiError` requires.
 */
export function getErrorStatus(error: unknown): number | null {
  if (typeof error !== "object" || error === null || !("status" in error)) {
    return null;
  }

  const { status } = error as { status: unknown };

  return typeof status === "number" ? status : null;
}

/**
 * Whether a failed request is worth sending again: a gateway timeout or a
 * network failure. Auth and client errors never are.
 */
export function isRetryableRequestError(error: unknown): boolean {
  const status = getErrorStatus(error);

  if (status === 401) {
    return false;
  }

  return status === 504 || isNetworkError(error);
}

export function isServerError(error: unknown): boolean {
  const status = getErrorStatus(error);

  return status !== null && status >= 500;
}
